/**
 * CouponInput — reusable "Enter coupon code" block.
 *
 * Renders one of two states:
 *   • not applied — input + Apply button + list of available coupons
 *   • applied     — success row with "You save ₹X" + Remove button
 *
 * Drives revenue by surfacing coupon codes inside Cart (not just Checkout)
 * so users don't bail at checkout to hunt for promos.
 */
import React, { useState } from 'react';
import { Check, Tag, X, Sparkles } from 'lucide-react';
import { Button, Card, css } from '../../design';
import { Coupon, CouponApplyResponse, couponsAPI } from '../../api/coupons';

export interface CouponInputProps {
  storeId: number;
  subtotal: number;
  appliedCoupon: Coupon | null;
  discountAmount: number;
  /** Pre-loaded available coupons (saves a round-trip). */
  availableCoupons?: Coupon[];
  onApply: (resp: CouponApplyResponse) => void;
  onRemove: () => void;
}

const CouponInput: React.FC<CouponInputProps> = ({
  storeId,
  subtotal,
  appliedCoupon,
  discountAmount,
  availableCoupons = [],
  onApply,
  onRemove,
}) => {
  const [code, setCode] = useState('');
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const apply = async (codeArg?: string) => {
    const c = (codeArg ?? code).trim();
    if (!c) return;
    setApplying(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await couponsAPI.applyCoupon(c, storeId, subtotal);
      if (res.valid) {
        setSuccess(res.message);
        onApply(res);
      } else {
        setError(res.message);
      }
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Could not apply coupon');
    } finally {
      setApplying(false);
    }
  };

  if (appliedCoupon) {
    return (
      <Card padding="md" style={{ borderColor: css.color.primary, background: css.color.primarySoft, borderWidth: 1, borderStyle: 'solid', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ width: 32, height: 32, borderRadius: 8, background: css.color.primary, color: '#FFFFFF', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Check size={16} />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: css.color.primaryStrong }}>
              {appliedCoupon.code} applied
            </div>
            <div style={{ fontSize: 12, color: css.color.text2 }}>
              You save <strong>₹{discountAmount.toFixed(0)}</strong>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onRemove} leadingIcon={<X size={14} />}>
            Remove
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card padding="md" style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, fontWeight: 700, color: css.color.text }}>
        <Tag size={16} color={css.color.accent} />
        Apply a coupon
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <input
          type="text"
          placeholder="Enter coupon code"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          onKeyDown={(e) => e.key === 'Enter' && apply()}
          style={{
            flex: 1,
            padding: '10px 14px',
            border: `1px solid ${css.color.border}`,
            borderRadius: 10,
            background: css.color.surface,
            color: css.color.text,
            fontWeight: 600,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            fontSize: 14,
            outline: 'none',
          }}
        />
        <Button variant="primary" size="md" onClick={() => apply()} disabled={!code.trim()} loading={applying}>
          Apply
        </Button>
      </div>
      {error && <div style={{ marginTop: 8, color: css.color.danger, fontSize: 12, fontWeight: 600 }}>{error}</div>}
      {success && <div style={{ marginTop: 8, color: css.color.primary, fontSize: 12, fontWeight: 600 }}>{success}</div>}

      {availableCoupons.length > 0 && (
        <div style={{ marginTop: 14, borderTop: `1px dashed ${css.color.border}`, paddingTop: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: css.color.textMuted, marginBottom: 8 }}>
            <Sparkles size={12} /> Available offers
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {availableCoupons.map((c) => {
              const eligible = subtotal >= c.min_order_amount;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => { setCode(c.code); if (eligible) apply(c.code); }}
                  disabled={!eligible}
                  style={{
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '8px 10px',
                    border: `1px dashed ${css.color.accent}`,
                    borderRadius: 10,
                    background: eligible ? css.color.accentSoft : css.color.surface2,
                    cursor: eligible ? 'pointer' : 'not-allowed',
                    opacity: eligible ? 1 : 0.7,
                  }}
                >
                  <span
                    style={{
                      padding: '4px 8px',
                      borderRadius: 6,
                      background: css.color.accent,
                      color: '#FFFFFF',
                      fontWeight: 800,
                      fontSize: 11,
                      letterSpacing: '0.04em',
                    }}
                  >
                    {c.code}
                  </span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 13, fontWeight: 700, color: css.color.text }}>
                      {c.description ||
                        (c.discount_type === 'percentage' ? `${c.discount_value}% off` : `₹${c.discount_value} off`)}
                    </span>
                    <span style={{ display: 'block', fontSize: 11, color: css.color.textMuted }}>
                      {c.min_order_amount > 0
                        ? `Min order ₹${c.min_order_amount}${!eligible ? ` — add ₹${(c.min_order_amount - subtotal).toFixed(0)} more` : ''}`
                        : 'No minimum order'}
                    </span>
                  </span>
                  {eligible ? (
                    <span style={{ color: css.color.primary, fontWeight: 700, fontSize: 12 }}>Apply →</span>
                  ) : (
                    <span style={{ color: css.color.textMuted, fontSize: 11 }}>Add more</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </Card>
  );
};

export default CouponInput;

